import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { PageHeader, StatCard, RiskBadge, StageBadge } from "@/components/common/Bits";
import { RISK_HSL, fmtDate } from "@/lib/constants";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import {
  Database, Rocket, FlaskConical, ClipboardList, Radar, Scale, RefreshCcw, Gauge,
  AlertTriangle, CalendarClock, ArrowUpRight, Search, FilePlus2, ShieldCheck, Vote, Activity, RotateCcw,
} from "lucide-react";
import {
  ResponsiveContainer, PieChart, Pie, Cell, Tooltip, BarChart, Bar, XAxis, YAxis,
  AreaChart, Area, CartesianGrid, ScatterChart, Scatter, ZAxis, Legend,
} from "recharts";

const CYCLE_STEPS = [
  { key: "Detectar", icon: Search, desc: "Shadow AI activa" },
  { key: "Registrar", icon: FilePlus2, desc: "En registro" },
  { key: "Evaluar", icon: ShieldCheck, desc: "En evaluación" },
  { key: "Decidir", icon: Vote, desc: "En decisión" },
  { key: "Monitorear", icon: Activity, desc: "Piloto / producción" },
  { key: "Reevaluar", icon: RotateCcw, desc: "Reevaluaciones" },
];

const tooltipStyle = {
  background: "hsl(0 0% 100%)", border: "1px solid hsl(212 37% 90%)", borderRadius: 10,
  fontSize: 12, boxShadow: "0 8px 24px -8px hsl(212 84% 15% / 0.15)",
};

export default function Dashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then(({ data }) => setData(data)).catch(() => {});
  }, []);

  if (!data) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-72" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">{[...Array(8)].map((_, i) => <Skeleton key={i} className="h-32 rounded-xl" />)}</div>
        <Skeleton className="h-80 rounded-xl" />
      </div>
    );
  }

  const t = data.totals;
  const riskData = Object.entries(data.by_risk).map(([name, value]) => ({ name, value }));
  const providerData = Object.entries(data.by_provider).map(([name, value]) => ({ name: name.replace(" (Microsoft)", ""), value })).sort((a, b) => b.value - a.value);

  return (
    <div className="space-y-8">
      <PageHeader
        title="Panel principal"
        subtitle="Visión ejecutiva y operacional del portafolio institucional de inteligencia artificial."
        testId="dashboard-header"
      >
        <Badge variant="outline" className="bg-accent/10 text-accent border-accent/25 py-1.5 px-3">
          <Gauge className="h-3.5 w-3.5 mr-1.5" /> Exposición global: {t.exposure}/100
        </Badge>
      </PageHeader>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="kpi-grid">
        <StatCard icon={Database} label="Iniciativas registradas" value={t.initiatives} hint="Portafolio total" testId="kpi-initiatives" />
        <StatCard icon={Rocket} label="En producción" value={t.production} hint="Incluye monitoreo" tone="accent" />
        <StatCard icon={FlaskConical} label="Pilotos activos" value={t.pilots} hint="En validación de valor" tone="accent" />
        <StatCard icon={ClipboardList} label="Evaluaciones abiertas" value={t.open_assessments} hint="Risk Assessment en curso" tone="warning" />
        <StatCard icon={Radar} label="Shadow AI detectada" value={t.shadow_detected} hint="Sin gobernanza formal" tone="danger" testId="kpi-shadow" />
        <StatCard icon={Scale} label="Decisiones pendientes" value={t.pending_decisions} hint="En votación del Comité" tone="warning" />
        <StatCard icon={RefreshCcw} label="Reevaluaciones activas" value={t.active_reevaluations} hint="Cambios que exigen revisión" tone="warning" />
        <div className="bg-card rounded-xl border border-border shadow-card p-5 flex flex-col gap-3">
          <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Exposición al riesgo</span>
          <div className="mt-auto">
            <span className="font-display text-3xl font-semibold tabular">{t.exposure}<span className="text-base text-muted-foreground">/100</span></span>
            <Progress value={t.exposure} className="h-2 mt-3" />
          </div>
        </div>
      </div>

      {/* Governance cycle */}
      <Card className="shadow-card border-border" data-testid="cycle-card">
        <CardHeader className="pb-4">
          <CardTitle className="font-display text-base font-semibold">Ciclo de gobierno</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {CYCLE_STEPS.map((s, idx) => (
              <div key={s.key} className="relative rounded-xl border border-border bg-gradient-panel p-4 flex flex-col">
                <div className="flex items-center justify-between">
                  <div className="h-8 w-8 rounded-lg bg-primary/8 text-primary flex items-center justify-center"><s.icon className="h-4 w-4" /></div>
                  <span className="text-[10px] font-semibold text-muted-foreground">0{idx + 1}</span>
                </div>
                <p className="mt-3 text-sm font-semibold">{s.key}</p>
                <p className="text-[11px] text-muted-foreground">{s.desc}</p>
                <p className="mt-2 font-display text-2xl font-semibold tabular">{data.cycle[s.key] ?? 0}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Charts row */}
      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="shadow-card border-border">
          <CardHeader className="pb-2"><CardTitle className="font-display text-base font-semibold">Distribución por riesgo residual</CardTitle></CardHeader>
          <CardContent className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={riskData} dataKey="value" nameKey="name" innerRadius={55} outerRadius={85} paddingAngle={3} strokeWidth={0}>
                  {riskData.map((d) => <Cell key={d.name} fill={RISK_HSL[d.name] || "hsl(220 25% 55%)"} />)}
                </Pie>
                <Tooltip contentStyle={tooltipStyle} />
                <Legend iconType="circle" iconSize={8} formatter={(v) => <span className="text-xs text-foreground">{v}</span>} />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="shadow-card border-border">
          <CardHeader className="pb-2"><CardTitle className="font-display text-base font-semibold">Distribución por proveedor</CardTitle></CardHeader>
          <CardContent className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={providerData} layout="vertical" margin={{ left: 8, right: 16 }}>
                <XAxis type="number" hide />
                <YAxis type="category" dataKey="name" width={130} tick={{ fontSize: 11, fill: "hsl(218 21% 46%)" }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "hsl(210 50% 97%)" }} />
                <Bar dataKey="value" fill="hsl(212 82% 24%)" radius={[0, 6, 6, 0]} barSize={14} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="shadow-card border-border">
          <CardHeader className="pb-2"><CardTitle className="font-display text-base font-semibold">Evolución del portafolio</CardTitle></CardHeader>
          <CardContent className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.trend} margin={{ left: -20, right: 8, top: 8 }}>
                <defs>
                  <linearGradient id="gIni" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(212 82% 24%)" stopOpacity={0.28} />
                    <stop offset="100%" stopColor="hsl(212 82% 24%)" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(212 37% 91%)" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: "hsl(218 21% 46%)" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "hsl(218 21% 46%)" }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={tooltipStyle} />
                <Area type="monotone" dataKey="iniciativas" name="Iniciativas" stroke="hsl(212 82% 24%)" strokeWidth={2} fill="url(#gIni)" />
                <Area type="monotone" dataKey="produccion" name="En producción" stroke="hsl(193 100% 36%)" strokeWidth={2} fill="transparent" />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Portfolio scatter + lists */}
      <div className="grid lg:grid-cols-5 gap-4">
        <Card className="shadow-card border-border lg:col-span-3" data-testid="portfolio-scatter">
          <CardHeader className="pb-2">
            <CardTitle className="font-display text-base font-semibold">Portafolio · valor esperado vs riesgo</CardTitle>
            <p className="text-xs text-muted-foreground">Tamaño de burbuja = usuarios estimados · MM CLP de valor anual estimado</p>
          </CardHeader>
          <CardContent className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <ScatterChart margin={{ left: -10, right: 16, top: 12 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(212 37% 91%)" />
                <XAxis type="number" dataKey="risk_score" name="Riesgo" domain={[0, 110]} tick={{ fontSize: 11, fill: "hsl(218 21% 46%)" }} axisLine={false} tickLine={false} label={{ value: "Riesgo residual →", position: "insideBottom", offset: -4, fontSize: 11, fill: "hsl(218 21% 46%)" }} />
                <YAxis type="number" dataKey="value" name="Valor" tick={{ fontSize: 11, fill: "hsl(218 21% 46%)" }} axisLine={false} tickLine={false} />
                <ZAxis type="number" dataKey="users" range={[80, 700]} />
                <Tooltip contentStyle={tooltipStyle} cursor={{ strokeDasharray: "3 3" }} content={({ payload }) => {
                  const p = payload?.[0]?.payload;
                  if (!p) return null;
                  return (
                    <div className="bg-card border border-border rounded-lg shadow-float p-3 text-xs">
                      <p className="font-semibold">{p.name}</p>
                      <p className="text-muted-foreground mt-1">{p.department} · {p.stage}</p>
                      <p className="mt-1">Riesgo: <span className="font-medium">{p.risk_level}</span> · Valor: {p.value} MM · {p.users} usuarios</p>
                    </div>
                  );
                }} />
                <Scatter data={data.portfolio}>
                  {data.portfolio.map((p) => <Cell key={p.id} fill={RISK_HSL[p.risk_level]} fillOpacity={0.75} />)}
                </Scatter>
              </ScatterChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <div className="lg:col-span-2 space-y-4">
          <Card className="shadow-card border-border">
            <CardHeader className="pb-3"><CardTitle className="font-display text-base font-semibold">Alertas y vencimientos</CardTitle></CardHeader>
            <CardContent className="space-y-2.5">
              {data.alerts.slice(0, 5).map((a, i) => (
                <Link to={a.link} key={i} className="flex items-start gap-3 rounded-lg border border-border p-3 hover:border-accent/50 transition-colors group" data-testid={`alert-${i}`}>
                  {a.type === "shadow" ? <Radar className="h-4 w-4 text-risk-critical mt-0.5 shrink-0" /> : a.type === "task" ? <AlertTriangle className="h-4 w-4 text-risk-high mt-0.5 shrink-0" /> : <CalendarClock className="h-4 w-4 text-risk-medium mt-0.5 shrink-0" />}
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium leading-snug">{a.text}</p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">{fmtDate(a.date)}</p>
                  </div>
                  <ArrowUpRight className="h-3.5 w-3.5 text-muted-foreground group-hover:text-accent shrink-0" />
                </Link>
              ))}
            </CardContent>
          </Card>

          <Card className="shadow-card border-border">
            <CardHeader className="pb-3"><CardTitle className="font-display text-base font-semibold">Mayor valor · mayor riesgo</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {data.top_value.slice(0, 3).map((i) => (
                <Link key={i.id} to={`/iniciativas/${i.id}`} className="flex items-center justify-between gap-2 rounded-lg px-3 py-2 hover:bg-muted transition-colors">
                  <div className="min-w-0">
                    <p className="text-xs font-medium truncate">{i.name}</p>
                    <p className="text-[10px] text-muted-foreground truncate">{i.roi}</p>
                  </div>
                  <Badge variant="outline" className="bg-accent/10 text-accent border-accent/25 text-[10px] shrink-0">{i.value}</Badge>
                </Link>
              ))}
              <div className="border-t border-border my-2" />
              {data.top_risk.slice(0, 3).map((i) => (
                <Link key={i.id} to={`/iniciativas/${i.id}`} className="flex items-center justify-between gap-2 rounded-lg px-3 py-2 hover:bg-muted transition-colors">
                  <div className="min-w-0">
                    <p className="text-xs font-medium truncate">{i.name}</p>
                    <p className="text-[10px] text-muted-foreground truncate">{i.department}</p>
                  </div>
                  <div className="flex gap-1.5 shrink-0"><RiskBadge level={i.risk} /><StageBadge stage={i.stage} /></div>
                </Link>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
